import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from './../../../core/service/base/data.service';

import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { Building, BuildingPostTarget } from './../../model/building';
import { Observable } from 'rxjs';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Company } from '../../model/company';
import { Property } from '../../model/property';
import { catchError, finalize, map } from 'rxjs/operators';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { DhMarketAreaOptions } from '../../../letting/model/platform-options';
import { SelectionItem } from '../../../core/component/select/select.component';
import { IPostTargetLoader } from '../base/post-target-loader.interface';
import { LocalDate } from '../../../core/model/local-date';

@Injectable()
export class BuildingService extends DataService<Building> implements IAutoCompleteService, IPostTargetLoader<BuildingPostTarget> {
  private readonly _default_FilterFields: string[] = ['company', 'property'];

  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private _messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, _messageService, Building, progressBar, 'building');
  }

  getSelectionItem(id: string): Observable<AutoCompleteSelectionItem> {
    return this.client.get<any>(`${this.endpoint}/selectionItem/${id}`).pipe(
      map(item => new AutoCompleteSelectionItem(item.name, item.id, item, item.shortName)),
      catchError(e => {
        this._messageService.showError(e);
        return [];
      })
    );
  }

  getSelection(filter: string, options?: any, page?: number): Observable<AutoCompleteSelectionItem[]> {
    let queryArgs = {
      filter: filter,
      maxItems: 20,
    };
    if (options) queryArgs['options'] = options;
    if (page && page > 0) queryArgs['page'] = page;

    return this.client.post<any[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => new AutoCompleteSelectionItem(item.name, item.id, item, item.shortName))),
      catchError(e => {
        this._messageService.showError(e);
        return [];
      })
    );
  }

  protected createModel(modelData: any): Building {
    try {
      return new Building(modelData);
    } catch (e) {
      console.log(e);
      throw new Error('Invalid data format.');
    }
  }

  getFilterDefinition(): Observable<{
    company: string;
    companies: Company[];
    property: string;
    properties: Property[];
    buildingCustomPropertiesTextValues: { key: string; value: string }[];
    buildingCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    buildingCustomPropertiesNumericValues: { key: string; value: string }[];
    buildingCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    selectedFields: string[];
    defaultFields: string[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : '',
          companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
          property: res.property ? res.property : '',
          properties: Array.isArray(res.properties) ? res.properties.map(x => Object.assign(new Property(), x)) : [],
          buildingCustomPropertiesTextValues: Array.isArray(res.buildingCustomPropertiesTextValues)
            ? res.buildingCustomPropertiesTextValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          buildingCustomPropertiesDateValues: Array.isArray(res.buildingCustomPropertiesDateValues)
            ? res.buildingCustomPropertiesDateValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          buildingCustomPropertiesNumericValues: Array.isArray(res.buildingCustomPropertiesNumericValues)
            ? res.buildingCustomPropertiesNumericValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          buildingCustomPropertiesBooleanValues: Array.isArray(res.buildingCustomPropertiesBooleanValues)
            ? res.buildingCustomPropertiesBooleanValues.map(value => ({ key: value.key, value: value.value }))
            : [],
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._default_FilterFields,
          defaultFields: this._default_FilterFields,
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getNewDefaults(propertyId: string): Observable<any> {
    return this.client.get(this.endpoint + '/new', { params: { propertyId: propertyId } });
  }

  moveToProperty(buildingId: string, destinationPropertyId: string): Observable<any> {
    this.progressBar.start();
    return this.client.post(this.endpoint + '/change-property', { destinationPropertyId, buildingId }).pipe(
      finalize(() => {
        this.progressBar.stop();
      })
    );
  }

  getDhMarketOptions(): Observable<DhMarketAreaOptions> {
    return this.client.get<DhMarketAreaOptions>(`${this.endpoint}/dhmarket-options`).pipe(map(val => new DhMarketAreaOptions(val)));
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Byggnader.xlsx');
  }

  getSelectionItemsByProperty(propertyId: string): Observable<SelectionItem<string>[]> {
    const result: Observable<Building[]> = this.getFilteredList({ property: propertyId });
    return result.pipe(
      map(items => {
        return items.map(item => item.toSelectionItem());
      })
    );
  }

  getPostTargetModels(propertyId: string): Observable<BuildingPostTarget[]> {
    this.progressBar.start();
    return this.client.get<BuildingPostTarget[]>(`${this.endpoint}/post-target/${propertyId}`).pipe(
      map(response => response.map(x => new BuildingPostTarget(x))),
      finalize(() => this.progressBar.stop())
    );
  }
}
